/**
 * "Head there" — the commit. Creates the target attempt (which is what links
 * a decision to a driver and powers anti-herding), persists it so the
 * TripCard survives a reload and works offline, and hands off to the maps
 * app targeting the STAGING SPOT, never the venue centroid.
 *
 * Maps opens through a real <a href> rendered before the tap (docs/11 §3.6):
 * window.open is unreliable in iOS standalone PWAs. The provider is a
 * Settings preference.
 */
import { api, ApiError, type Opp } from './api.js';
import { store, type StoredAttempt, type MapsProvider } from './storage.js';

export type HeadThereResult =
  | { ok: true; attempt: StoredAttempt }
  | { ok: false; reason: 'already_heading' | 'offline' | 'error'; message: string };

export function toStoredAttempt(a: { id: string; expires_at: string }, opp: Opp): StoredAttempt {
  return {
    id: a.id,
    occurrence_id: opp.occurrence.id,
    title: opp.occurrence.venue_name ?? opp.occurrence.title,
    subtitle: opp.occurrence.title,
    staging_label: opp.staging.label,
    staging_approach: opp.staging.approach,
    staging_lat: opp.staging.lat,
    staging_lng: opp.staging.lng,
    leave_by: opp.leave_by,
    p10_at: opp.egress.p10_at,
    p90_at: opp.egress.p90_at,
    expires_at: a.expires_at,
  };
}

export async function headThere(decisionId: string, opp: Opp): Promise<HeadThereResult> {
  if (!navigator.onLine) {
    return { ok: false, reason: 'offline', message: 'No signal — can’t start a trip right now.' };
  }
  try {
    const a = await api.createAttempt(decisionId, opp.decision_candidate_id);
    const attempt = toStoredAttempt(a, opp);
    store.attempt.set(attempt);
    return { ok: true, attempt };
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      return { ok: false, reason: 'already_heading', message: 'You’re already heading somewhere.' };
    }
    return {
      ok: false, reason: 'error',
      message: err instanceof ApiError ? err.message : 'Couldn’t start — check your signal.',
    };
  }
}

export async function endAttempt(id: string, reason: 'completed' | 'cancelled'): Promise<void> {
  try { await api.endAttempt(id, reason); } catch { /* already closed server-side */ }
  store.attempt.clear();
}

/** Deep link for the chosen maps app, targeting the staging spot. */
export function mapsUrl(provider: MapsProvider, lat: number, lng: number): string {
  switch (provider) {
    case 'apple': return `maps://?daddr=${lat},${lng}&dirflg=d`;
    case 'waze': return `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
    default: return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
  }
}

export const MAPS_LABEL: Record<MapsProvider, string> = {
  apple: 'Apple Maps', google: 'Google Maps', waze: 'Waze',
};
